import { combineReducers } from "redux";
import { ActionSchema } from "../actions";
import {
  PasswordPolicyAction,
  AppInfoSchema,
  PeriodicSchema,
  PasswordCompliexitySchema,
  AppBasicSettingsAction,
  RiskBasedAuthenticationAction,
  RiskAuthSchema,
  MFAActionSchema,
  PinAuthAction,
  PinAuthSchema,
  UpdatePinAuthSchema,
  SecurityQuestionsSchema,
  SecurityQuestionsAction,
  WebhookSecretsSchema,
  WebhookSecretsAction,
  SecurityQuestionAction,
  SecurityQuestionSchema,
  MFASecurityQuestionsAction,
  MFASecurityQuestionsSchema,
  UpdateMFASecurityQuestionsAction,
  MFAAuthenticatorAppsSchema,
  MFAAuthenticatorAppsAction,
  UpdateMFAAuthenticatorAppsAction,
  mfaSettingsAction,
  updateMFASettingsAction,
  MFASettingsSchema,
  MFAEmailOTPStatusSchema,
  mfaEmailOtpStatusAction,
  updateMFAEmailOtpStatusAction,
  mfaSmsOtpStatusAction,
  updateMFASmsOtpStatusAction,
  MFASmsOTPStatusSchema,
  PushNotificationSchema,
  pushNotificationAction,
  updatepushNotificationAction,
  addpushNotificationAction,
  BreachedPasswordSchema,
  duoAuthenticationAction,
  DuoAuthenticationSchema,
  updateduoAuthenticationAction,
  CaptchaSettingsSchema,
  getCaptchaSettingAction,
  updateCaptchaSettingAction,
  UpdateForceLogoutSettingSchema,
  UpdateRemeberMeSettingSchema,
  updateForceLogoutSettingConstAction,
  updateRemeberMeSettingConstAction,
  updateSessionLimitSettingAction,
  UpdateSessionLimitSettingSchema
} from "./../actions/securityConfigurationActions";
import { AppBasicInfoSchema } from "../actions/authActions";
import passwordPolicyReducer, { PasswordPolicyState } from "../features/security/passwordPolicy/passwordPolicies";
import secondFactorAuthenticationReducer, { SecondFactorAuthenticationState } from "../features/security/multiFactorAuthentication/secondFactorAuthentication";
import duoSecondFactorAuthenticationReducer, { DuoSecondFactorAuthenticationState } from "../features/security/multiFactorAuthentication/duoSecondFactorAuthentication";
import googleAuthenticatorReducer, { GoogleAuthenticatorState } from "../features/security/multiFactorAuthentication/googleAuthenticator";
import pushNotificationReducer, { PushNotificationState } from "../features/security/multiFactorAuthentication/pushNotification";
import securityQuestionsReducer, { SecurityQuestionsState } from "../features/security/multiFactorAuthentication/securityQuestions";
import { getOutcomeTrackingState, getRequestTrackingState, MutationTrackingState } from "../../services/analytics/tracking";

const withRequestTracking = <T extends object>(state: T, action: ActionSchema<any>) => ({
  ...state,
  ...getRequestTrackingState(action as any)
});

const withSuccessTracking = <T extends object>(state: T, action: ActionSchema<any>) => ({
  ...state,
  ...action.payload,
  ...getOutcomeTrackingState(state as MutationTrackingState, "success")
});

const withErrorTracking = <T extends object>(state: T, action: ActionSchema<any>) => ({
  ...state,
  ...action.payload,
  ...getOutcomeTrackingState(state as MutationTrackingState, "error")
});

const appInfoReducer = (state = {}, action: ActionSchema<AppInfoSchema>) => {
  switch (action.type) {
    case PasswordPolicyAction.GET_APP_INFO_LOADING:
    case PasswordPolicyAction.GET_APP_INFO_ERROR:
    case PasswordPolicyAction.GET_APP_INFO_SUCCESS:
      return {
        ...state,
        ...action.payload
      };

    default:
      return state;
  }
};

const securityQuestionState: SecurityQuestionSchema = {
  Data: {
    questions: [],
    renderCount: 0,
    limit: 0,
    message: "",
    isCountUpdate: false
  }
};

const securityQuestion = (state = securityQuestionState, action) => {
  switch (action.type) {
    case SecurityQuestionAction.UPDATE_COUNT_LIMIT_REQUEST:
    case SecurityQuestionAction.FETCH_QUESTIONS_REQUEST:
    case SecurityQuestionAction.ADD_QUESTION_REQUEST:
    case SecurityQuestionAction.UPDATE_QUESTION_REQUEST:
    case SecurityQuestionAction.DELETE_QUESTION_REQUEST:
      return {
        ...state,
        isLoading: true,
        error: null,
        Data: {
          ...state.Data,

          isCountUpdate: false
        }
      };

    case SecurityQuestionAction.FETCH_QUESTIONS_SUCCESS:
      return {
        ...state,
        isLoading: false,
        isSuccess: true,
        ...action.payload
      };
    case SecurityQuestionAction.ADD_QUESTION_SUCCESS:
      return {
        isLoading: false,
        isSuccess: true,
        Data: {
          ...state.Data,
          message: action.payload
        }
      };
    case SecurityQuestionAction.UPDATE_QUESTION_SUCCESS:
      return {
        isLoading: false,
        isSuccess: true,

        Data: {
          ...state.Data,
          ...action.payload
        }
      };
    case SecurityQuestionAction.UPDATE_COUNT_LIMIT_SUCCESS:
      return {
        isLoading: false,
        isSuccess: true,
        Data: {
          ...state.Data,
          message: action.payload,
          isCountUpdate: true
        }
      };
    case SecurityQuestionAction.DELETE_QUESTION_SUCCESS:
      return {
        isLoading: false,
        isSuccess: true,

        Data: {
          ...state.Data,
          message: action.payload
        }
      };
    case SecurityQuestionAction.UPDATE_COUNT_LIMIT_FAILURE:
      return {
        isLoading: false,
        error: action.payload,
        isError: true,
        Data: {
          ...state.Data,
          isCountUpdate: true
        }
      };
    case SecurityQuestionAction.FETCH_QUESTIONS_ERROR:
    case SecurityQuestionAction.ADD_QUESTION_ERROR:
    case SecurityQuestionAction.DELETE_QUESTION_ERROR:
    case SecurityQuestionAction.UPDATE_QUESTION_ERROR:
      return {
        ...state,
        isLoading: false,
        isError: true,
        error: action.payload
      };
    default:
      return state;
  }
};

const getMFAActionEnabledReducer = (state = {}, action: ActionSchema<MFAActionSchema>) => {
  switch (action.type) {
    case RiskBasedAuthenticationAction.GET_MFA_ACTIONS:
    case RiskBasedAuthenticationAction.UPDATE_MFA_ACTIONS:
      return {
        ...state,
        ...action.payload
      };

    default:
      return state;
  }
};
const getRiskAuthSettingsReducer = (state = {}, action: ActionSchema<RiskAuthSchema>) => {
  switch (action.type) {
    case RiskBasedAuthenticationAction.GET_RISK_AUTH_SETTINGS_LOADING:
    case RiskBasedAuthenticationAction.GET_RISK_AUTH_SETTINGS_SUCCESS:
    case RiskBasedAuthenticationAction.GET_RISK_AUTH_SETTINGS_ERROR:
      return {
        ...state,
        ...action.payload
      };

    default:
      return state;
  }
};

const updateRiskAuthSettingsReducer = (state = {}, action: ActionSchema<RiskAuthSchema>) => {
  switch (action.type) {
    case RiskBasedAuthenticationAction.UPDATE_RISK_AUTH_SETTINGS:
      return withRequestTracking(state, action);
    case RiskBasedAuthenticationAction.UPDATE_RISK_AUTH_SETTINGS_LOADING:
      return {
        ...state,
        ...action.payload
      };
    case RiskBasedAuthenticationAction.UPDATE_RISK_AUTH_SETTINGS_SUCCESS:
      return withSuccessTracking(state, action);
    case RiskBasedAuthenticationAction.UPDATE_RISK_AUTH_SETTINGS_ERROR:
      return withErrorTracking(state, action);

    default:
      return state;
  }
};

const getPasswordComplexityReducer = (state = {}, action: ActionSchema<PasswordCompliexitySchema>) => {
  switch (action.type) {
    case PasswordPolicyAction.GET_PASSWORD_COMPLEXITY_LOADIN:
    case PasswordPolicyAction.GET_PASSWORD_COMPLEXITY_ERROR:
    case PasswordPolicyAction.GET_PASSWORD_COMPLEXITY_SUCCESS:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};
const updateAppBasicSettingsReducer = (state = {}, action: ActionSchema<AppBasicInfoSchema>) => {
  switch (action.type) {
    case AppBasicSettingsAction.UPDATE_APP_BASIC_INFO:
      return withRequestTracking(state, action);
    case AppBasicSettingsAction.UPDATE_APP_BASIC_INFO_LOADING:
      delete state["errorCode"];
      return { ...state, ...action.payload };
    case AppBasicSettingsAction.UPDATE_APP_BASIC_INFO_SUCCESS:
      delete state["errorCode"];
      return withSuccessTracking(state, action);
    case AppBasicSettingsAction.UPDATE_APP_BASIC_INFO_ERROR:
      delete state["errorCode"];
      return withErrorTracking(state, action);
    default:
      return state;
  }
};
const PriodicPasswordReducer = (state = {}, action: ActionSchema<PeriodicSchema>) => {
  switch (action.type) {
    case PasswordPolicyAction.GET_PERIODIC_PASSWORD_LOADING:
    case PasswordPolicyAction.GET_PERIODIC_PASSWORD_ERROR:
    case PasswordPolicyAction.GET_PERIODIC_PASSWORD_SUCCESS:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};
const pinAuthReducer = (state = {}, action: ActionSchema<PinAuthSchema>) => {
  switch (action.type) {
    case PinAuthAction.PIN_AUTH_LOADING:
    case PinAuthAction.PIN_AUTH_ERROR:
    case PinAuthAction.PIN_AUTH_SUCCESS:
      delete state["errorCode"];
      return { ...state, ...action.payload };
    default:
      return state;
  }
};
const updatePinAuthReducer = (state = {}, action: ActionSchema<PinAuthSchema>) => {
  switch (action.type) {
    case PinAuthAction.UPDATE_PIN_AUTH:
      return withRequestTracking(state, action);
    case PinAuthAction.UPDATE_PIN_AUTH_LOADING:
      delete state["IsPosted"];
      return { ...state, ...action.payload };
    case PinAuthAction.UPDATE_PIN_AUTH_SUCCESS:
      delete state["errorCode"];
      return withSuccessTracking(state, action);
    case PinAuthAction.UPDATE_PIN_AUTH_ERROR:
      delete state["errorCode"];
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const getSecurityQuestionsReducer = (state = {}, action: ActionSchema<SecurityQuestionsSchema>) => {
  switch (action.type) {
    case SecurityQuestionsAction.GET_SECURITY_QUESTIONS_SUCCESS:
    case SecurityQuestionsAction.GET_SECURITY_QUESTIONS_LOADING:
    case SecurityQuestionsAction.GET_SECURITY_QUESTIONS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getWebhookSecretOptionsReducer = (state = {}, action: ActionSchema<WebhookSecretsSchema>) => {
  switch (action.type) {
    case WebhookSecretsAction.GET_WEBHOOK_SECRETS_OPTIONS_SUCCESS:
    case WebhookSecretsAction.GET_WEBHOOK_SECRETS_OPTIONS_LOADING:
    case WebhookSecretsAction.GET_WEBHOOK_SECRETS_OPTIONS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getMFASecurityQuestionsReducer = (state = {}, action: ActionSchema<MFASecurityQuestionsSchema>) => {
  switch (action.type) {
    case MFASecurityQuestionsAction.GET_MFA_SECURITY_QUESTIONS_SUCCESS:
    case MFASecurityQuestionsAction.GET_MFA_SECURITY_QUESTIONS_LOADING:
    case MFASecurityQuestionsAction.GET_MFA_SECURITY_QUESTIONS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const updateMFASecurityQuestionsReducer = (state = {}, action: ActionSchema<MFASecurityQuestionsSchema>) => {
  switch (action.type) {
    case UpdateMFASecurityQuestionsAction.UPDATE_MFA_SECURITY_QUESTIONS:
      return withRequestTracking(state, action);
    case UpdateMFASecurityQuestionsAction.UPDATE_MFA_SECURITY_QUESTIONS_LOADING:
      return { ...state, ...action.payload };
    case UpdateMFASecurityQuestionsAction.UPDATE_MFA_SECURITY_QUESTIONS_SUCCESS:
      return withSuccessTracking(state, action);
    case UpdateMFASecurityQuestionsAction.UPDATE_MFA_SECURITY_QUESTIONS_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const getMFAAuthenticatorAppsReducer = (state = {}, action: ActionSchema<MFAAuthenticatorAppsSchema>) => {
  switch (action.type) {
    case MFAAuthenticatorAppsAction.GET_MFA_AUTHENTICATOR_APPS_SUCCESS:
    case MFAAuthenticatorAppsAction.GET_MFA_AUTHENTICATOR_APPS_LOADING:
    case MFAAuthenticatorAppsAction.GET_MFA_AUTHENTICATOR_APPS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const updateMFAAuthenticatorAppsReducer = (state = {}, action: ActionSchema<MFAAuthenticatorAppsSchema>) => {
  switch (action.type) {
    case UpdateMFAAuthenticatorAppsAction.UPDATE_MFA_AUTHENTICATOR_APPS:
      return withRequestTracking(state, action);
    case UpdateMFAAuthenticatorAppsAction.UPDATE_MFA_AUTHENTICATOR_APPS_LOADING:
      return { ...state, ...action.payload };
    case UpdateMFAAuthenticatorAppsAction.UPDATE_MFA_AUTHENTICATOR_APPS_SUCCESS:
      return withSuccessTracking(state, action);
    case UpdateMFAAuthenticatorAppsAction.UPDATE_MFA_AUTHENTICATOR_APPS_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const getMFASettingsReducer = (state = {}, action: ActionSchema<MFASettingsSchema>) => {
  switch (action.type) {
    case mfaSettingsAction.GET_MFA_SETTINGS_SUCCESS:
    case mfaSettingsAction.GET_MFA_SETTINGS_LOADING:
    case mfaSettingsAction.GET_MFA_SETTINGS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const updateMFASettingsReducer = (state = {}, action: ActionSchema<MFASettingsSchema>) => {
  switch (action.type) {
    case updateMFASettingsAction.UPDATE_MFA_SETTINGS:
      return withRequestTracking(state, action);
    case updateMFASettingsAction.UPDATE_MFA_SETTINGS_LOADING:
      return { ...state, ...action.payload };
    case updateMFASettingsAction.UPDATE_MFA_SETTINGS_SUCCESS:
      return withSuccessTracking(state, action);
    case updateMFASettingsAction.UPDATE_MFA_SETTINGS_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const getMFAEmailOtpStatusReducer = (state = {}, action: ActionSchema<MFAEmailOTPStatusSchema>) => {
  switch (action.type) {
    case mfaEmailOtpStatusAction.GET_MFA_EMAIL_STATUS_SUCCESS:
    case mfaEmailOtpStatusAction.GET_MFA_EMAIL_STATUS_LOADING:
    case mfaEmailOtpStatusAction.GET_MFA_EMAIL_STATUS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const updateMFAEmailOtpStatusReducer = (state = {}, action: ActionSchema<MFAEmailOTPStatusSchema>) => {
  switch (action.type) {
    case updateMFAEmailOtpStatusAction.UPDATE_MFA_EMAIL_STATUS:
      return withRequestTracking(state, action);
    case updateMFAEmailOtpStatusAction.UPDATE_MFA_EMAIL_STATUS_LOADING:
      return { ...state, ...action.payload };
    case updateMFAEmailOtpStatusAction.UPDATE_MFA_EMAIL_STATUS_SUCCESS:
      return withSuccessTracking(state, action);
    case updateMFAEmailOtpStatusAction.UPDATE_MFA_EMAIL_STATUS_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const getMFASmsOtpStatusReducer = (state = {}, action: ActionSchema<MFASmsOTPStatusSchema>) => {
  switch (action.type) {
    case mfaSmsOtpStatusAction.GET_MFA_SMS_STATUS_SUCCESS:
    case mfaSmsOtpStatusAction.GET_MFA_SMS_STATUS_LOADING:
    case mfaSmsOtpStatusAction.GET_MFA_SMS_STATUS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const updateMFASmsOtpStatusReducer = (state = {}, action: ActionSchema<MFASmsOTPStatusSchema>) => {
  switch (action.type) {
    case updateMFASmsOtpStatusAction.UPDATE_MFA_SMS_STATUS:
      return withRequestTracking(state, action);
    case updateMFASmsOtpStatusAction.UPDATE_MFA_SMS_STATUS_LOADING:
      return { ...state, ...action.payload };
    case updateMFASmsOtpStatusAction.UPDATE_MFA_SMS_STATUS_SUCCESS:
      return withSuccessTracking(state, action);
    case updateMFASmsOtpStatusAction.UPDATE_MFA_SMS_STATUS_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const getPushNotificationReducer = (state = {}, action: ActionSchema<PushNotificationSchema>) => {
  switch (action.type) {
    case pushNotificationAction.GET_PUSH_NOTIFICATION_SUCCESS:
    case pushNotificationAction.GET_PUSH_NOTIFICATION_LOADING:
    case pushNotificationAction.GET_PUSH_NOTIFICATION_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const addPushNotificationReducer = (state = {}, action: ActionSchema<PushNotificationSchema>) => {
  switch (action.type) {
    case addpushNotificationAction.ADD_PUSH_NOTIFICATION:
      return withRequestTracking(state, action);
    case addpushNotificationAction.ADD_PUSH_NOTIFICATION_LOADING:
      return { ...state, ...action.payload };
    case addpushNotificationAction.ADD_PUSH_NOTIFICATION_SUCCESS:
      return withSuccessTracking(state, action);
    case addpushNotificationAction.ADD_PUSH_NOTIFICATION_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const updatePushNotificationReducer = (state = {}, action: ActionSchema<PushNotificationSchema>) => {
  switch (action.type) {
    case updatepushNotificationAction.UPDATE_PUSH_NOTIFICATION:
      return withRequestTracking(state, action);
    case updatepushNotificationAction.UPDATE_PUSH_NOTIFICATION_LOADING:
      return { ...state, ...action.payload };
    case updatepushNotificationAction.UPDATE_PUSH_NOTIFICATION_SUCCESS:
      return withSuccessTracking(state, action);
    case updatepushNotificationAction.UPDATE_PUSH_NOTIFICATION_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const getDuoAuthenticationReducer = (state = {}, action: ActionSchema<DuoAuthenticationSchema>) => {
  switch (action.type) {
    case duoAuthenticationAction.GET_DUO_AUTHENTICATION_SUCCESS:
    case duoAuthenticationAction.GET_DUO_AUTHENTICATION_LOADING:
    case duoAuthenticationAction.GET_DUO_AUTHENTICATION_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const updateDuoAuthenticationReducer = (state = {}, action: ActionSchema<DuoAuthenticationSchema>) => {
  switch (action.type) {
    case updateduoAuthenticationAction.UPDATE_DUO_AUTHENTICATION:
      return withRequestTracking(state, action);
    case updateduoAuthenticationAction.UPDATE_DUO_AUTHENTICATION_LOADING:
      return { ...state, ...action.payload };
    case updateduoAuthenticationAction.UPDATE_DUO_AUTHENTICATION_SUCCESS:
      return withSuccessTracking(state, action);
    case updateduoAuthenticationAction.UPDATE_DUO_AUTHENTICATION_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};
const getBreachedPasswordReducer = (state = {}, action: ActionSchema<BreachedPasswordSchema>) => {
  switch (action.type) {
    case PasswordPolicyAction.GET_BREACHED_PASSWORD_POLICY_LOADING:
    case PasswordPolicyAction.GET_BREACHED_PASSWORD_POLICY_ERROR:
    case PasswordPolicyAction.GET_BREACHED_PASSWORD_POLICY_SUCCESS:
      return { ...state, ...action.payload };

    case PasswordPolicyAction.SYNC_BREACHED_PASSWORD_CONFIG:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const postBreachedPasswordReducer = (state = {}, action: ActionSchema<BreachedPasswordSchema>) => {
  switch (action.type) {
    case PasswordPolicyAction.POST_BREACHED_PASSWORD_POLICY:
      return withRequestTracking(state, action);
    case PasswordPolicyAction.POST_BREACHED_PASSWORD_POLICY_LOADING:
      return { ...state, ...action.payload };
    case PasswordPolicyAction.POST_BREACHED_PASSWORD_POLICY_SUCCESS:
      return withSuccessTracking(state, action);
    case PasswordPolicyAction.POST_BREACHED_PASSWORD_POLICY_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const getCaptchaSettingsReducer = (state = {}, action: ActionSchema<CaptchaSettingsSchema>) => {
  switch (action.type) {
    case getCaptchaSettingAction.GET_CAPTCHA_SETTING_LOADING:
    case getCaptchaSettingAction.GET_CAPTCHA_SETTING_ERROR:
    case getCaptchaSettingAction.GET_CAPTCHA_SETTING_SUCCESS:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const updateCaptchaSettingsReducer = (state = {}, action: ActionSchema<CaptchaSettingsSchema>) => {
  switch (action.type) {
    case updateCaptchaSettingAction.UPDATE_CAPTCHA_SETTING:
      return withRequestTracking(state, action);
    case updateCaptchaSettingAction.UPDATE_CAPTCHA_SETTING_LOADING:
      return { ...state, ...action.payload };
    case updateCaptchaSettingAction.UPDATE_CAPTCHA_SETTING_SUCCESS:
      return withSuccessTracking(state, action);
    case updateCaptchaSettingAction.UPDATE_CAPTCHA_SETTING_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const updateForceLogoutReducer = (state = {}, action: ActionSchema<UpdateForceLogoutSettingSchema>) => {
  switch (action.type) {
    case updateForceLogoutSettingConstAction.UPDATE_FORCE_LOGOUT:
      return withRequestTracking(state, action);
    case updateForceLogoutSettingConstAction.UPDATE_FORCE_LOGOUT_LOADING:
      return { ...state, ...action.payload };
    case updateForceLogoutSettingConstAction.UPDATE_FORCE_LOGOUT_SUCCESS:
      return withSuccessTracking(state, action);
    case updateForceLogoutSettingConstAction.UPDATE_FORCE_LOGOUT_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const updateRemeberMeReducer = (state = {}, action: ActionSchema<UpdateRemeberMeSettingSchema>) => {
  switch (action.type) {
    case updateRemeberMeSettingConstAction.UPDATE_REMEBER_ME:
      return withRequestTracking(state, action);
    case updateRemeberMeSettingConstAction.UPDATE_REMEBER_ME_LOADING:
      return { ...state, ...action.payload };
    case updateRemeberMeSettingConstAction.UPDATE_REMEBER_ME_SUCCESS:
      return withSuccessTracking(state, action);
    case updateRemeberMeSettingConstAction.UPDATE_REMEBER_ME_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const updateSessionLimitReducer = (state = {}, action: ActionSchema<UpdateSessionLimitSettingSchema>) => {
  switch (action.type) {
    case updateSessionLimitSettingAction.UPDATE_SESSION_LIMIT:
      return withRequestTracking(state, action);
    case updateSessionLimitSettingAction.UPDATE_SESSION_LIMIT_LOADING:
      return { ...state, ...action.payload };
    case updateSessionLimitSettingAction.UPDATE_SESSION_LIMIT_SUCCESS:
      return withSuccessTracking(state, action);
    case updateSessionLimitSettingAction.UPDATE_SESSION_LIMIT_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

export const securityConfigurationReducer = combineReducers({
  PasswordComplexity: getPasswordComplexityReducer,
  PriodicPassword: PriodicPasswordReducer,
  appInfo: appInfoReducer,
  updateAppBasicSettings: updateAppBasicSettingsReducer,
  getRiskAuthSettings: getRiskAuthSettingsReducer,
  updateRiskAuthSettings: updateRiskAuthSettingsReducer,
  MFAEnabled: getMFAActionEnabledReducer,
  pinAuth: pinAuthReducer,
  updatePinAuth: updatePinAuthReducer,
  securityQuestions: getSecurityQuestionsReducer,
  webhookSecrets: getWebhookSecretOptionsReducer,
  securityQuestion: securityQuestion,
  mfaSecurityQuestions: getMFASecurityQuestionsReducer,
  updateMfaSecurityQuestions: updateMFASecurityQuestionsReducer,
  mfaAuthenticatorApps: getMFAAuthenticatorAppsReducer,
  updateMfaAuthenticatorApps: updateMFAAuthenticatorAppsReducer,
  mfaSettings: getMFASettingsReducer,
  updateMfaSettings: updateMFASettingsReducer,
  mfaEmailStatus: getMFAEmailOtpStatusReducer,
  updateMfaEmailStatus: updateMFAEmailOtpStatusReducer,
  mfaSmsStatus: getMFASmsOtpStatusReducer,
  updateMfaSmsStatus: updateMFASmsOtpStatusReducer,
  pushNotification: getPushNotificationReducer,
  updatepushNotification: updatePushNotificationReducer,
  addPushNotification: addPushNotificationReducer,
  getBreachedPassword: getBreachedPasswordReducer,
  postBreachedPassword: postBreachedPasswordReducer,
  getDuoAuthentication: getDuoAuthenticationReducer,
  updateDuoAuthentication: updateDuoAuthenticationReducer,
  getCaptchaSettingsData: getCaptchaSettingsReducer,
  updateCaptchaSettingsData: updateCaptchaSettingsReducer,
  updateForceLogoutData: updateForceLogoutReducer,
  updateRemeberMeData: updateRemeberMeReducer,
  updateSessionLimitData: updateSessionLimitReducer,
  passwordPolicy: passwordPolicyReducer,
  secondFactorAuthentication: secondFactorAuthenticationReducer,
  duoSecondFactorAuthentication: duoSecondFactorAuthenticationReducer,
  googleAuthenticator: googleAuthenticatorReducer,
  pushNotify: pushNotificationReducer,
  securityquestions: securityQuestionsReducer
});
export interface SecurityConfigurationSchema {
  PasswordComplexity: PasswordCompliexitySchema;
  PriodicPassword: PeriodicSchema;
  appInfo: AppInfoSchema;
  updateAppBasicSettings: AppBasicInfoSchema;
  getRiskAuthSettings: RiskAuthSchema;
  updateRiskAuthSettings: RiskAuthSchema;
  MFAEnabled: MFAActionSchema;
  pinAuth: PinAuthSchema;
  updatePinAuth: UpdatePinAuthSchema;
  securityQuestions: SecurityQuestionsSchema;
  webhookSecrets: WebhookSecretsSchema;
  securityQuestion: SecurityQuestionSchema;
  mfaSecurityQuestions: MFASecurityQuestionsSchema;
  updateMfaSecurityQuestions: MFASecurityQuestionsSchema;
  mfaAuthenticatorApps: MFAAuthenticatorAppsSchema;
  updateMfaAuthenticatorApps: MFAAuthenticatorAppsSchema;
  mfaSettings: MFASettingsSchema;
  updateMfaSettings: MFASettingsSchema;
  mfaEmailStatus: MFAEmailOTPStatusSchema;
  updateMfaEmailStatus: MFAEmailOTPStatusSchema;
  mfaSmsStatus: MFASmsOTPStatusSchema;
  updateMfaSmsStatus: MFASmsOTPStatusSchema;
  pushNotification: PushNotificationSchema;
  updatepushNotification: PushNotificationSchema;
  addPushNotification: PushNotificationSchema;
  getBreachedPassword: BreachedPasswordSchema;
  postBreachedPassword: BreachedPasswordSchema;
  getDuoAuthentication: DuoAuthenticationSchema;
  updateDuoAuthentication: DuoAuthenticationSchema;
  getCaptchaSettingsData: CaptchaSettingsSchema;
  updateCaptchaSettingsData: CaptchaSettingsSchema;
  passwordPolicy: PasswordPolicyState;
  updateForceLogoutData: any;
  updateRemeberMeData: any;
  updateSessionLimitData: any;
  secondFactorAuthentication: SecondFactorAuthenticationState;
  duoSecondFactorAuthentication: DuoSecondFactorAuthenticationState;
  googleAuthenticator: GoogleAuthenticatorState;
  pushNotify: PushNotificationState;
  securityquestions: SecurityQuestionsState;
}
